/**
 * Component: PerformanceMonitoringPage
 * SpecReferenceID: ADMIN-V1
 * Requirements: ADMIN-V1-R12
 *
 * Admin performance monitoring dashboard displaying query volume,
 * average model latency, token throughput, and index retrieval
 * performance metrics with time-series visualization.
 *
 * ADMIN-V1-R12: System shall display performance monitoring metrics
 *               including query volume, average model latency, token
 *               throughput, and index retrieval performance.
 *
 * Accessibility: WCAG 2.1 AA compliant
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Loader2,
  AlertCircle,
  Activity,
  Clock,
  Zap,
  Database,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
} from 'recharts';

import api, { getErrorMessage } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

/**
 * Performance metrics response types matching backend API
 */
interface QueryVolumeMetrics {
  total_queries: number;
  queries_per_minute: number;
  queries_by_type: Record<string, number>;
}

interface ModelLatencyMetrics {
  average_ms: number;
  p50_ms: number;
  p95_ms: number;
  p99_ms: number;
  max_ms: number;
  min_ms: number;
}

interface TokenThroughputMetrics {
  total_tokens: number;
  tokens_per_minute: number;
  average_per_request: number;
  prompt_tokens: number;
  completion_tokens: number;
}

interface IndexRetrievalMetrics {
  average_latency_ms: number;
  p50_latency_ms: number;
  p95_latency_ms: number;
  average_results: number;
  total_requests: number;
}

interface ErrorMetrics {
  count: number;
  rate: number;
}

interface TimeWindowInfo {
  start: string | null;
  end: string | null;
  duration_minutes: number;
}

interface PerformanceMetricsResponse {
  query_volume: QueryVolumeMetrics;
  model_latency: ModelLatencyMetrics;
  token_throughput: TokenThroughputMetrics;
  index_retrieval: IndexRetrievalMetrics;
  errors: ErrorMetrics;
  time_window: TimeWindowInfo;
}

interface PerformanceMetricsSummaryResponse {
  last_hour: PerformanceMetricsResponse;
  last_6_hours: PerformanceMetricsResponse;
  last_24_hours: PerformanceMetricsResponse;
  timestamp: string;
}

interface TimeSeriesDataPoint {
  timestamp: string;
  value: number;
  count: number;
}

interface TimeSeriesResponse {
  metric_type: string;
  data_points: TimeSeriesDataPoint[];
  window_minutes: number;
  bucket_minutes: number;
}

/**
 * Available time window options for metrics display
 */
const TIME_WINDOWS = [
  { label: '1h', minutes: 60 },
  { label: '6h', minutes: 360 },
  { label: '24h', minutes: 1440 },
] as const;

/**
 * MetricCard component for displaying summary statistics
 */
interface MetricCardProps {
  title: string;
  value: string;
  icon: React.ReactNode;
  description?: string;
  subValue?: string;
}

function MetricCard({ title, value, icon, description, subValue }: MetricCardProps): React.ReactElement {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {subValue && (
          <p className="text-sm font-medium text-muted-foreground">{subValue}</p>
        )}
        {description && (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Format milliseconds to a human-readable string
 */
function formatLatency(ms: number): string {
  if (ms === 0) return '0 ms';
  if (ms < 1) return `${ms.toFixed(2)} ms`;
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

/**
 * Format a timestamp for chart display
 */
function formatChartTime(timestamp: unknown): string {
  const date = new Date(typeof timestamp === 'string' || typeof timestamp === 'number' ? timestamp : '');
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * Custom tooltip for charts
 */
function ChartTooltip({ active, payload, label }: {
  active?: boolean;
  payload?: Array<{ value: number; name: string; color: string }>;
  label?: string;
}): React.ReactElement | null {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border bg-popover p-3 shadow-md" role="tooltip">
      <p className="text-sm font-medium">{label}</p>
      {payload.map((entry, index) => (
        <p key={index} className="text-sm" style={{ color: entry.color }}>
          {entry.name}: {typeof entry.value === 'number' ? entry.value.toFixed(1) : entry.value}
        </p>
      ))}
    </div>
  );
}

/**
 * PerformanceMonitoringPage Component
 *
 * ADMIN-V1-R12: Provides performance monitoring dashboard with
 * query volume, model latency, token throughput, and index retrieval metrics.
 */
export function PerformanceMonitoringPage(): React.ReactElement {
  const [selectedWindow, setSelectedWindow] = useState(0); // index into TIME_WINDOWS
  const [summary, setSummary] = useState<PerformanceMetricsSummaryResponse | null>(null);
  const [latencyTimeSeries, setLatencyTimeSeries] = useState<TimeSeriesDataPoint[]>([]);
  const [queryTimeSeries, setQueryTimeSeries] = useState<TimeSeriesDataPoint[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const currentWindow = TIME_WINDOWS[selectedWindow] ?? TIME_WINDOWS[0];

  /**
   * Fetch all performance metrics data
   */
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [summaryRes, latencyRes, queryRes] = await Promise.all([
        api.get<PerformanceMetricsSummaryResponse>('/admin/metrics/performance/summary'),
        api.get<TimeSeriesResponse>('/admin/metrics/performance/timeseries', {
          params: {
            metric_type: 'model_call',
            window_minutes: currentWindow.minutes,
            bucket_minutes: currentWindow.minutes <= 60 ? 5 : currentWindow.minutes <= 360 ? 15 : 30,
          },
        }),
        api.get<TimeSeriesResponse>('/admin/metrics/performance/timeseries', {
          params: {
            metric_type: 'query',
            window_minutes: currentWindow.minutes,
            bucket_minutes: currentWindow.minutes <= 60 ? 5 : currentWindow.minutes <= 360 ? 15 : 30,
          },
        }),
      ]);

      setSummary(summaryRes.data);
      setLatencyTimeSeries(latencyRes.data.data_points);
      setQueryTimeSeries(queryRes.data.data_points);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [currentWindow.minutes]);

  // Fetch data on mount and when window changes
  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      void fetchData();
    }, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  // Get metrics for selected time window
  const metrics: PerformanceMetricsResponse | null = summary
    ? selectedWindow === 0
      ? summary.last_hour
      : selectedWindow === 1
        ? summary.last_6_hours
        : summary.last_24_hours
    : null;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      {/* Page Header */}
      <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
            Performance Monitoring
          </h1>
          <p className="mt-1 text-sm text-muted-foreground sm:text-base">
            Query volume, model latency, token throughput, and search performance
          </p>
        </div>
        <div className="flex gap-2 self-start sm:self-auto">
          <Button
            variant={autoRefresh ? 'default' : 'outline'}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            aria-label={autoRefresh ? 'Disable auto-refresh' : 'Enable auto-refresh (30s)'}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${autoRefresh ? 'animate-spin' : ''}`} aria-hidden="true" />
            {autoRefresh ? 'Auto' : 'Auto-refresh'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void fetchData()}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                Loading...
              </>
            ) : (
              'Refresh'
            )}
          </Button>
        </div>
      </header>

      {/* Time Window Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Time Window</CardTitle>
          <CardDescription>Select a time range for metrics display</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2" role="radiogroup" aria-label="Time window selection">
            {TIME_WINDOWS.map((window, index) => (
              <Button
                key={window.label}
                variant={selectedWindow === index ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedWindow(index)}
                role="radio"
                aria-checked={selectedWindow === index}
              >
                Last {window.label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" role="alert">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Summary Metric Cards */}
      {metrics && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          <MetricCard
            title="Total Queries"
            value={metrics.query_volume.total_queries.toLocaleString()}
            icon={<Activity className="h-4 w-4 text-muted-foreground" />}
            subValue={`${metrics.query_volume.queries_per_minute.toFixed(1)} / min`}
            description="Query volume"
          />
          <MetricCard
            title="Model Latency (P50)"
            value={formatLatency(metrics.model_latency.p50_ms)}
            icon={<Clock className="h-4 w-4 text-muted-foreground" />}
            subValue={`P95: ${formatLatency(metrics.model_latency.p95_ms)}`}
            description={`P99: ${formatLatency(metrics.model_latency.p99_ms)}`}
          />
          <MetricCard
            title="Token Throughput"
            value={metrics.token_throughput.total_tokens.toLocaleString()}
            icon={<Zap className="h-4 w-4 text-muted-foreground" />}
            subValue={`${metrics.token_throughput.tokens_per_minute.toFixed(1)} / min`}
            description={`Avg ${metrics.token_throughput.average_per_request.toFixed(0)} per request`}
          />
          <MetricCard
            title="Search Retrieval"
            value={formatLatency(metrics.index_retrieval.average_latency_ms)}
            icon={<Database className="h-4 w-4 text-muted-foreground" />}
            subValue={`${metrics.index_retrieval.total_requests.toLocaleString()} requests`}
            description={`Avg ${metrics.index_retrieval.average_results.toFixed(1)} results`}
          />
          <MetricCard
            title="Error Rate"
            value={`${(metrics.errors.rate * 100).toFixed(2)}%`}
            icon={<AlertTriangle className="h-4 w-4 text-muted-foreground" />}
            subValue={`${metrics.errors.count} errors`}
            description="Failed requests"
          />
        </div>
      )}

      {/* Charts Grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Response Latency Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Response Latency (Model)</CardTitle>
            <CardDescription>
              Average model call latency over time (last {currentWindow.label})
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading && latencyTimeSeries.length === 0 ? (
              <div className="h-[300px] animate-pulse bg-muted rounded" aria-label="Loading chart" />
            ) : latencyTimeSeries.length === 0 ? (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No latency data available for this time window
              </div>
            ) : (
              <div aria-label="Response latency chart showing model call latency over time">
                <span className="sr-only">
                  Area chart displaying average model response latency in milliseconds
                </span>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={latencyTimeSeries}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis
                      dataKey="timestamp"
                      tickFormatter={formatChartTime}
                      className="text-xs"
                    />
                    <YAxis
                      tickFormatter={(v: number) => `${Math.round(v)}ms`}
                      className="text-xs"
                    />
                    <Tooltip
                      content={<ChartTooltip />}
                      labelFormatter={formatChartTime}
                    />
                    <Area
                      type="monotone"
                      dataKey="value"
                      name="Avg Latency (ms)"
                      stroke="hsl(var(--primary))"
                      fill="hsl(var(--primary) / 0.2)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Query Volume Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Query Volume</CardTitle>
            <CardDescription>
              Number of queries per time bucket (last {currentWindow.label})
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading && queryTimeSeries.length === 0 ? (
              <div className="h-[300px] animate-pulse bg-muted rounded" aria-label="Loading chart" />
            ) : queryTimeSeries.length === 0 ? (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No query volume data available for this time window
              </div>
            ) : (
              <div aria-label="Query volume chart showing requests over time">
                <span className="sr-only">
                  Bar chart displaying number of queries per time bucket
                </span>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={queryTimeSeries}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis
                      dataKey="timestamp"
                      tickFormatter={formatChartTime}
                      className="text-xs"
                    />
                    <YAxis className="text-xs" />
                    <Tooltip
                      content={<ChartTooltip />}
                      labelFormatter={formatChartTime}
                    />
                    <Bar
                      dataKey="value"
                      name="Queries"
                      fill="hsl(var(--primary))"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detailed Metrics Tables */}
      {metrics && (
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Latency Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Latency Breakdown</CardTitle>
              <CardDescription>Model and search latency percentiles</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium mb-2">Model Latency</h4>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="rounded-md bg-muted p-2 text-center">
                      <div className="text-xs text-muted-foreground">P50</div>
                      <div className="font-medium">{formatLatency(metrics.model_latency.p50_ms)}</div>
                    </div>
                    <div className="rounded-md bg-muted p-2 text-center">
                      <div className="text-xs text-muted-foreground">P95</div>
                      <div className="font-medium">{formatLatency(metrics.model_latency.p95_ms)}</div>
                    </div>
                    <div className="rounded-md bg-muted p-2 text-center">
                      <div className="text-xs text-muted-foreground">P99</div>
                      <div className="font-medium">{formatLatency(metrics.model_latency.p99_ms)}</div>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-2">Search Retrieval Latency</h4>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="rounded-md bg-muted p-2 text-center">
                      <div className="text-xs text-muted-foreground">P50</div>
                      <div className="font-medium">{formatLatency(metrics.index_retrieval.p50_latency_ms)}</div>
                    </div>
                    <div className="rounded-md bg-muted p-2 text-center">
                      <div className="text-xs text-muted-foreground">P95</div>
                      <div className="font-medium">{formatLatency(metrics.index_retrieval.p95_latency_ms)}</div>
                    </div>
                    <div className="rounded-md bg-muted p-2 text-center">
                      <div className="text-xs text-muted-foreground">Avg</div>
                      <div className="font-medium">{formatLatency(metrics.index_retrieval.average_latency_ms)}</div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Token Usage Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Token Usage</CardTitle>
              <CardDescription>Prompt and completion token breakdown</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-md bg-muted p-3">
                    <div className="text-xs text-muted-foreground">Prompt Tokens</div>
                    <div className="text-lg font-bold">{metrics.token_throughput.prompt_tokens.toLocaleString()}</div>
                  </div>
                  <div className="rounded-md bg-muted p-3">
                    <div className="text-xs text-muted-foreground">Completion Tokens</div>
                    <div className="text-lg font-bold">{metrics.token_throughput.completion_tokens.toLocaleString()}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-md bg-muted p-3">
                    <div className="text-xs text-muted-foreground">Total Tokens</div>
                    <div className="text-lg font-bold">{metrics.token_throughput.total_tokens.toLocaleString()}</div>
                  </div>
                  <div className="rounded-md bg-muted p-3">
                    <div className="text-xs text-muted-foreground">Avg / Request</div>
                    <div className="text-lg font-bold">{metrics.token_throughput.average_per_request.toFixed(0)}</div>
                  </div>
                </div>

                {/* Query Type Breakdown */}
                {Object.keys(metrics.query_volume.queries_by_type).length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium mb-2">Queries by Type</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(metrics.query_volume.queries_by_type).map(([type, count]) => (
                        <div key={type} className="flex items-center justify-between rounded-md bg-muted p-2 text-sm">
                          <span className="capitalize">{type}</span>
                          <span className="font-medium">{count.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Loading state when no data yet */}
      {isLoading && !metrics && (
        <div className="flex items-center justify-center py-12" aria-label="Loading performance metrics">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}
    </div>
  );
}

export default PerformanceMonitoringPage;
